import { useCallback, useEffect, useRef, useState } from "react"
import { View } from "react-native"
import { Text, TextInput, Button } from "@src/components"
import { PriceInput } from "../price-input"
import { ar } from "@src/i18n"
import { logPolygonCoordinates, toCoordinates } from "./utils"
import { trpc } from "@src/utils"
import { useForm, Controller, useWatch, Control } from "react-hook-form"
import { Polygon } from "./polygon"

type FormValues = { name: string; fees: number; fill: string; points?: google.maps.LatLngLiteral[] }

type DeliveryAreaFormProps = {
  mode: "add" | "edit"
  editId?: number
  onDone: () => void
}

export function DeliveryAreaForm({ mode, editId, onDone }: DeliveryAreaFormProps) {
  const polygonRef = useRef<google.maps.Polygon | undefined>(undefined)
  const areas = trpc.deliveryAreas.list.useQuery()
  const area = areas.data?.find((a) => a.id === editId)
  const utils = trpc.useUtils()
  const create = trpc.deliveryAreas.create.useMutation()
  const edit = trpc.deliveryAreas.edit.useMutation()

  const { control, handleSubmit, formState } = useForm<FormValues>({
    defaultValues: { name: area?.name ?? "", fees: area?.fees ?? 0, fill: area?.fill ?? "#3B82F6", points: area?.geojson ? toCoordinates(area.geojson) : undefined },
  })

  const { isValid, isSubmitting } = formState

  const onSave = handleSubmit(async ({ name, fees, fill }) => {
    if (!polygonRef.current) return
    const pts = logPolygonCoordinates(polygonRef.current)

    if (mode === "add") {
      await create.mutateAsync({ name, points: pts, fill, fees })
    } else if (editId !== undefined) {
      await edit.mutateAsync({ id: editId, name, points: pts, fill, fees })
    }

    await utils.deliveryAreas.list.invalidate()
    await utils.deliveryAreas.list.refetch()
    onDone()
  })

  const isLoading = isSubmitting || create.isPending || edit.isPending

  return (
    <>
      <TempPolygon control={control} polygonRef={polygonRef} initialFill={area?.fill ?? "#3B82F6"} />
      <View className="absolute top-4 right-4 w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-50">
        <Text className="font-bold text-gray-800 p-2">{mode === "add" ? ar.addDeliveryArea : ar.editDeliveryArea}</Text>
        <Text className="text-xs font-medium text-gray-500 mb-1 px-2">{ar.deliveryAreaName}</Text>
        <Controller
          control={control}
          name="name"
          render={({ field: { value, onChange } }) => <TextInput value={value} onChangeText={onChange} className="border px-2 mx-2 mb-2 h-9" />}
        />
        <Text className="text-xs font-medium text-gray-500 mb-1 px-2">{ar.deliveryFees}</Text>
        <Controller
          control={control}
          name="fees"
          render={({ field: { value, onChange } }) => <PriceInput value={value} onValueChange={(v) => onChange(v ?? 0)} className="border h-9 px-2 mx-2 mb-1.5" placeholder="0" />}
        />

        <Controller control={control} name="fill" render={({ field }) => <ColorInput value={field.value} onChange={field.onChange} className="w-full h-9 px-1.5 mb-1.5" />} />

        <View className="flex-row gap-1.5 px-2 pb-2">
          <Button className="flex-1 bg-gray-100" onPress={onDone}>
            <Text className="text-gray-700">{ar.cancel}</Text>
          </Button>
          <Button className="flex-1 bg-blue-600" loading={isLoading} disabled={!isValid} onPress={onSave}>
            <Text className="text-white">{ar.save}</Text>
          </Button>
        </View>
      </View>
    </>
  )
}

function ColorInput({ value, onChange, onLiveChange, className }: { value: string; onChange?: (v: string) => void; onLiveChange?: (v: string) => void; className?: string }) {
  const [localColor, setLocalColor] = useState(value)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  useEffect(() => {
    setLocalColor(value)
    clearTimeout(debounceRef.current)
  }, [value])

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newColor = e.target.value
      setLocalColor(newColor)
      onLiveChange?.(newColor)
      clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(() => {
        onChange?.(newColor)
      }, 100)
    },
    [onChange, onLiveChange],
  )

  return <input type="color" className={className} value={localColor} onChange={handleChange} />
}

function TempPolygon({
  control,
  polygonRef,
  initialFill,
}: {
  control: Control<FormValues, unknown, FormValues>
  polygonRef: React.MutableRefObject<google.maps.Polygon | undefined>
  initialFill: string
}) {
  const points = useWatch({ control, name: "points" })
  const fill = useWatch({ control, name: "fill" })

  return (
    <Polygon
      points={points}
      fill={fill ?? initialFill}
      editable
      draggable
      onPolygonRef={(p) => {
        polygonRef.current = p
      }}
    />
  )
}
